import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as jobsApi from '@/api/jobs.api';
import type { JobWithRelations } from '@/api/jobs.api';
import * as employeesApi from '@/api/employees.api';
import { ApiError } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/hooks/useToast';
import { ZonaSelect } from '@/components/jobs/ZonaSelect';

/**
 * Edición completa de un trabajo (tanda edicion-completa). Manda al PATCH SOLO
 * lo que cambió (diff contra el estado inicial): así un trabajo cerrado nunca
 * dispara el 400 de campos operativos por arrastre, y la bitácora de auditoría
 * registra los campos realmente tocados. Guardar pide confirmación explícita.
 */

/** ISO → valor de <input type="datetime-local"> en hora LOCAL (o ''). */
function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** '' → null; texto → trimmed (la misma normalización que aplica el server). */
function norm(v: string): string | null {
  const t = v.trim();
  return t === '' ? null : t;
}

export function EditarJobModal({
  job,
  onClose,
}: {
  /** Trabajo a editar; null = modal cerrado. */
  job: JobWithRelations | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const open = Boolean(job);
  const cerrado = job?.estado === 'COMPLETADO' || job?.estado === 'NO_COMPLETADO';

  const [descripcion, setDescripcion] = useState('');
  const [direccion, setDireccion] = useState('');
  const [zona, setZona] = useState('');
  const [notas, setNotas] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [mapsUrl, setMapsUrl] = useState('');
  const [targetLat, setTargetLat] = useState('');
  const [targetLng, setTargetLng] = useState('');
  const [radio, setRadio] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Precarga desde el job cada vez que se abre (o cambia de trabajo).
  useEffect(() => {
    if (!job) return;
    setDescripcion(job.descripcion);
    setDireccion(job.direccion ?? '');
    setZona(job.zona ?? '');
    setNotas(job.notas ?? '');
    setScheduledAt(isoToLocalInput(job.scheduledAt));
    setEmployeeId(job.employeeId ?? '');
    setMapsUrl(job.despachoJson?.mapsUrl ?? '');
    setTargetLat(job.targetLat != null ? String(job.targetLat) : '');
    setTargetLng(job.targetLng != null ? String(job.targetLng) : '');
    setRadio(job.geofenceRadiusM != null ? String(job.geofenceRadiusM) : '');
    setConfirmando(false);
    setError(null);
  }, [job]);

  const { data: tecnicos } = useQuery({
    queryKey: ['employees', 'tecnicos'],
    queryFn: () => employeesApi.listEmployees({ rol: 'TECNICO', activo: true, take: 100 }),
    enabled: open && !cerrado,
  });

  function close() {
    setConfirmando(false);
    setError(null);
    onClose();
  }

  /** Diff contra el job: devuelve el patch con SOLO lo cambiado, o un error. */
  function buildPatch(): { patch?: Parameters<typeof jobsApi.updateJob>[1]; error?: string } {
    if (!job) return { patch: undefined };
    const patch: Parameters<typeof jobsApi.updateJob>[1] = {};

    if (descripcion.trim() === '') return { error: 'La descripción no puede quedar vacía' };
    if (descripcion.trim() !== job.descripcion) patch.descripcion = descripcion.trim();
    if (norm(direccion) !== (job.direccion ?? null)) patch.direccion = norm(direccion);
    if (norm(zona) !== (job.zona ?? null)) patch.zona = norm(zona);
    if (norm(notas) !== (job.notas ?? null)) patch.notas = norm(notas);

    if (!cerrado) {
      if (scheduledAt !== isoToLocalInput(job.scheduledAt)) {
        patch.scheduledAt = scheduledAt ? new Date(scheduledAt).toISOString() : null;
      }
      if ((employeeId || null) !== (job.employeeId ?? null)) {
        patch.employeeId = employeeId || null;
      }
      if (norm(mapsUrl) !== (job.despachoJson?.mapsUrl ?? null)) patch.mapsUrl = norm(mapsUrl);

      // Geocerca: se compara como trío. Vacía = limpiar; a medias = error.
      const latIni = job.targetLat != null ? String(job.targetLat) : '';
      const lngIni = job.targetLng != null ? String(job.targetLng) : '';
      const radIni = job.geofenceRadiusM != null ? String(job.geofenceRadiusM) : '';
      if (targetLat !== latIni || targetLng !== lngIni || radio !== radIni) {
        if (targetLat.trim() === '' && targetLng.trim() === '' && radio.trim() === '') {
          patch.targetLat = null;
          patch.targetLng = null;
          patch.geofenceRadiusM = null;
        } else {
          const lat = parseFloat(targetLat);
          const lng = parseFloat(targetLng);
          const r = parseInt(radio, 10);
          if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(r) || r <= 0) {
            return { error: 'Geocerca incompleta: lat, lng y radio (o los tres vacíos para quitarla)' };
          }
          patch.targetLat = lat;
          patch.targetLng = lng;
          patch.geofenceRadiusM = r;
        }
      }
    }
    return { patch };
  }

  const save = useMutation({
    mutationFn: (patch: Parameters<typeof jobsApi.updateJob>[1]) =>
      jobsApi.updateJob(job!.id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      void qc.invalidateQueries({ queryKey: ['tracking'] });
      toast.success('Trabajo actualizado');
      close();
    },
    onError: (err) => {
      setConfirmando(false);
      setError(err instanceof ApiError ? err.message : 'No se pudo guardar el trabajo');
    },
  });

  function handleGuardar() {
    const { patch, error: e } = buildPatch();
    if (e) {
      setError(e);
      return;
    }
    if (!patch || Object.keys(patch).length === 0) {
      toast.success('Sin cambios que guardar');
      close();
      return;
    }
    setError(null);
    if (!confirmando) {
      setConfirmando(true);
      return;
    }
    save.mutate(patch);
  }

  const tecnicoOptions = [
    { value: '', label: 'Sin asignar' },
    ...(tecnicos?.items ?? []).map((t) => ({ value: t.id, label: t.nombre })),
  ];

  return (
    <Modal
      open={open}
      onClose={close}
      title="Editar trabajo"
      description={
        cerrado
          ? 'Trabajo cerrado: solo se edita el registro administrativo.'
          : 'Corrige los datos del trabajo. Se aplican al confirmar.'
      }
      footer={
        confirmando ? (
          <>
            <Button variant="ghost" type="button" onClick={() => setConfirmando(false)}>
              Volver
            </Button>
            <Button onClick={handleGuardar} loading={save.isPending}>
              Confirmar cambios
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" type="button" onClick={close}>
              Cancelar
            </Button>
            <Button data-guia="job-editar.guardar" onClick={handleGuardar}>Guardar</Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        {cerrado && (
          <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            Este trabajo ya está {job?.estado === 'COMPLETADO' ? 'completado' : 'cerrado sin completar'}:
            se puede corregir descripción, dirección, zona y notas. La cita, el técnico y la
            geocerca quedan fijos y <b>el histórico de ponches no cambia</b>.
          </p>
        )}
        {confirmando && (
          <p className="rounded-md bg-brand-50 p-2 text-xs text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
            ¿Confirmar los cambios? Se aplican al instante y quedan registrados en la bitácora.
          </p>
        )}
        <Textarea
          label="Descripción del trabajo"
          rows={3}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
        />
        <Input label="Dirección" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
        {/* S-11 · Catálogo controlado; una zona vieja fuera de la lista se
            conserva y se muestra marcada (el histórico no se reescribe solo). */}
        <ZonaSelect value={zona} onChange={setZona} />
        <Textarea
          label="Notas / indicaciones (las ve el técnico)"
          placeholder="Llevar taladro, tocar el timbre del costado…"
          rows={2}
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
        />
        {!cerrado && (
          <>
            <Input
              label="Fecha y hora de la cita"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
            <Select
              data-guia="job-editar.tecnico"
              label="Técnico asignado"
              options={tecnicoOptions}
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            />
            <Input
              label="Link de ubicación (Google Maps)"
              placeholder="https://maps.app.goo.gl/…"
              value={mapsUrl}
              onChange={(e) => setMapsUrl(e.target.value)}
            />
            <details className="rounded-md border border-surface-border p-2 dark:border-slate-700">
              <summary className="cursor-pointer text-sm text-slate-600 dark:text-slate-300">
                Geocerca / objetivo
              </summary>
              <p className="mt-1 text-xs text-slate-400">
                Coordenadas manuales del destino. Si se deja vacío y hay link de Maps con
                coordenadas, el pin sale del link (lo manual gana).
              </p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <Input label="Lat" placeholder="18.4861" value={targetLat} onChange={(e) => setTargetLat(e.target.value)} />
                <Input label="Lng" placeholder="-69.9312" value={targetLng} onChange={(e) => setTargetLng(e.target.value)} />
                <Input label="Radio (m)" placeholder="150" value={radio} onChange={(e) => setRadio(e.target.value)} />
              </div>
            </details>
          </>
        )}
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
